// src/services/logisticsService.js
// Servicio de logística: gestión de tareas logísticas para eventos confirmados.
// Las tareas logísticas son activities con is_logistics_task=true y logistics_category asignada.

import { getSupabaseClient } from "./supabaseClient.js";
import { LOGISTICS_TASK_TEMPLATES } from "../domain/constants.js";

// -------------------------------------------------------
// Tipos y constantes
// -------------------------------------------------------

export const LOGISTICS_CATEGORIES = [
  "documentation",
  "lineup",
  "technical",
  "transport",
  "finance",
  "communication",
];

export const LOGISTICS_CATEGORY_LABELS = {
  documentation: "Documentación",
  lineup: "Lineup",
  technical: "Técnica",
  transport: "Transporte",
  finance: "Finanzas",
  communication: "Comunicación",
};

export const LOGISTICS_CATEGORY_ICONS = {
  documentation: "fa-file-contract",
  lineup: "fa-microphone",
  technical: "fa-sliders",
  transport: "fa-truck",
  finance: "fa-euro-sign",
  communication: "fa-comments",
};

// Tarima, luces y sonido ya no son tareas: se eligen en event_suppliers
// (tarjeta Proveedores de la ficha). Por eso aquí ya no se rellenan Paco,
// Xabi o Gilca según la zona.

// -------------------------------------------------------
// Obtener eventos confirmados (para el dashboard de logística)
// -------------------------------------------------------

/**
 * Obtiene eventos en stage 'confirmed' o 'done' recientes,
 * ordenados por fecha ASC (más próximos primero).
 * Incluye el contacto principal y el venue principal.
 */
export async function getLogisticsEvents(teamId = null, { sinceDays = 30 } = {}) {
  const supabase = getSupabaseClient();

  const sinceDate = Number.isFinite(Number(sinceDays))
    ? new Date(Date.now() - Number(sinceDays) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    : null;

  let query = supabase
    .from("events")
    .select(`
      id, date, client_name_legacy, amount, location, city, stage, notes,
      type, channel, region, owner_id, team_id,
      event_contacts(contact_id, is_primary, contacts:contact_id(id, name, surname, email, phone, company)),
      event_venues(venue_id, is_primary, venues:venue_id(id, name, city, region))
    `)
    .in("stage", ["confirmed"]);

  if (teamId) {
    query = query.eq("team_id", teamId);
  }

  if (sinceDate) {
    query = query.gte("date", sinceDate);
  }

  const { data: events, error } = await query.order("date", { ascending: true });

  if (error) throw error;

  return (events || []).map((ev) => {
    // Extract primary contact
    const contactLinks = ev.event_contacts || [];
    const primaryContact = (
      contactLinks.find((ec) => ec.is_primary) ||
      contactLinks[0]
    )?.contacts || null;

    // Extract primary venue
    const venueLinks = ev.event_venues || [];
    const primaryVenue = (
      venueLinks.find((ev2) => ev2.is_primary) ||
      venueLinks[0]
    )?.venues || null;

    return {
      ...ev,
      primaryContact,
      primaryVenue,
      // Remove nested to keep flat
      event_contacts: undefined,
      event_venues: undefined,
    };
  });
}

// -------------------------------------------------------
// Obtener tareas logísticas de un evento
// -------------------------------------------------------

/**
 * Obtiene todas las tareas logísticas (is_logistics_task=true) de un evento.
 */
export async function getLogisticsTasks(eventId) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("activities")
    .select("*")
    .eq("event_id", eventId)
    .eq("is_logistics_task", true)
    .order("logistics_category", { ascending: true })
    .order("created_at", { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Obtiene las tareas logísticas de múltiples eventos en una sola query.
 * Retorna un Map de { eventId → tasks[] }
 */
export async function getLogisticsTasksBulk(eventIds) {
  if (!eventIds || eventIds.length === 0) return new Map();

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("activities")
    .select("*")
    .in("event_id", eventIds)
    .eq("is_logistics_task", true)
    .order("logistics_category", { ascending: true });

  if (error) throw error;

  const map = new Map();
  for (const task of data || []) {
    if (!map.has(task.event_id)) map.set(task.event_id, []);
    map.get(task.event_id).push(task);
  }
  return map;
}

// -------------------------------------------------------
// Crear tareas iniciales (al confirmar un evento)
// -------------------------------------------------------

/**
 * Crea las tareas logísticas iniciales para un evento confirmado.
 * NO duplica si ya existen tareas logísticas en el evento.
 * Retorna el número de tareas creadas (0 si ya existían).
 */
export async function createDefaultLogisticsTasks(eventId, teamId) {
  const supabase = getSupabaseClient();
  let resolvedTeamId = teamId;

  if (!resolvedTeamId) {
    const { data: eventRow, error: eventErr } = await supabase
      .from("events")
      .select("team_id")
      .eq("id", eventId)
      .single();

    if (eventErr) throw eventErr;
    resolvedTeamId = eventRow?.team_id || null;
  }

  if (!resolvedTeamId) {
    throw new Error("teamId es obligatorio para crear tareas logisticas.");
  }

  // Check if logistics tasks already exist
  const { data: existing, error: checkErr } = await supabase
    .from("activities")
    .select("id")
    .eq("event_id", eventId)
    .eq("is_logistics_task", true)
    .limit(1);

  if (checkErr) throw checkErr;

  if (existing && existing.length > 0) {
    // Tasks already exist, don't duplicate
    return 0;
  }

  // Get current user for created_by
  const { data: { user } } = await supabase.auth.getUser();

  // Create all template tasks
  const tasks = LOGISTICS_TASK_TEMPLATES.map((tpl) => ({
    event_id: eventId,
    team_id: resolvedTeamId,
    type: "task",
    status: "pending",
    subject: tpl.subject,
    description: tpl.description,
    is_logistics_task: true,
    logistics_category: tpl.category,
    created_by: user?.id || null,
    updated_by: user?.id || null,
  }));

  const { data, error } = await supabase
    .from("activities")
    .insert(tasks)
    .select("id");

  if (error) throw error;
  return (data || []).length;
}

/**
 * Ensures the current streamlined logistics tasks exist for older events.
 * Existing events may still have the previous long checklist, so this inserts
 * only the missing core tasks without touching historical ones.
 */
export async function ensureCoreLogisticsTasks(eventId, teamId) {
  const supabase = getSupabaseClient();
  let resolvedTeamId = teamId;

  if (!eventId) return 0;

  if (!resolvedTeamId) {
    const { data: eventRow, error: eventErr } = await supabase
      .from("events")
      .select("team_id")
      .eq("id", eventId)
      .single();

    if (eventErr) throw eventErr;
    resolvedTeamId = eventRow?.team_id || null;
  }

  if (!resolvedTeamId) {
    throw new Error("teamId es obligatorio para crear tareas logisticas.");
  }

  const { data: existing, error: existingErr } = await supabase
    .from("activities")
    .select("id, subject, logistics_category")
    .eq("event_id", eventId)
    .eq("is_logistics_task", true);

  if (existingErr) throw existingErr;

  const misplacedVanTaskIds = (existing || [])
    .filter(t => String(t.subject || "").trim().toLowerCase() === "furgoneta" && t.logistics_category !== "technical")
    .map(t => t.id);
  if (misplacedVanTaskIds.length) {
    const { error: updateErr } = await supabase
      .from("activities")
      .update({ logistics_category: "technical" })
      .in("id", misplacedVanTaskIds);
    if (updateErr) throw updateErr;
  }

  const existingSubjects = new Set((existing || []).map(t => String(t.subject || "").trim().toLowerCase()));
  const missing = LOGISTICS_TASK_TEMPLATES.filter(tpl => !existingSubjects.has(String(tpl.subject || "").trim().toLowerCase()));

  if (!missing.length) return 0;

  const { data: { user } } = await supabase.auth.getUser();
  const rows = missing.map((tpl) => ({
    event_id: eventId,
    team_id: resolvedTeamId,
    type: "task",
    status: "pending",
    subject: tpl.subject,
    description: tpl.description,
    is_logistics_task: true,
    logistics_category: tpl.category,
    created_by: user?.id || null,
    updated_by: user?.id || null,
  }));

  const { data, error } = await supabase
    .from("activities")
    .insert(rows)
    .select("id");

  if (error) throw error;
  return (data || []).length;
}

// -------------------------------------------------------
// Actualizar tarea logística
// -------------------------------------------------------

/**
 * Actualiza el estado/notas de una tarea logística.
 */
export async function updateLogisticsTask(taskId, updates) {
  const supabase = getSupabaseClient();
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from("activities")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
      updated_by: user?.id || null,
    })
    .eq("id", taskId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// -------------------------------------------------------
// Cálculo del porcentaje de preparación
// -------------------------------------------------------

/**
 * Calcula el % de preparación de un evento dado sus tareas logísticas.
 * - Porcentaje = completadas / total
 */
export function calcPreparationStatus(tasks) {
  const total = tasks.length;
  if (total === 0) return { percent: 0, label: "Sin tareas", color: "gray" };

  const completed = tasks.filter((t) => t.status === "completed").length;
  const percent = Math.round((completed / total) * 100);

  let label, color;
  if (percent === 100) {
    label = "Completo";
    color = "emerald";
  } else if (percent >= 70) {
    label = "Avanzado";
    color = "amber";
  } else if (percent >= 30) {
    label = "En progreso";
    color = "blue";
  } else {
    label = "Inicio";
    color = "slate";
  }

  return { percent, label, color, completed, total };
}

/**
 * Agrupa tareas por categoría logística.
 */
export function groupTasksByCategory(tasks) {
  const groups = {};
  for (const cat of LOGISTICS_CATEGORIES) {
    groups[cat] = [];
  }
  for (const task of tasks) {
    const cat = task.logistics_category || "other";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(task);
  }
  return groups;
}

/**
 * Calcula el estado de cada categoría (para los badges de la tarjeta de evento).
 * Retorna { category: { completed, total } }
 */
export function calcCategoryStatus(tasks) {
  const grouped = groupTasksByCategory(tasks);
  const result = {};
  for (const [cat, catTasks] of Object.entries(grouped)) {
    result[cat] = {
      total: catTasks.length,
      completed: catTasks.filter((t) => t.status === "completed").length,
    };
  }
  return result;
}
